<i18n
  src="@/locales/views/packing_house/production/packing_by_pallet/assign_package_group.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/package_group.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>

<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_container')"
      :params="{
        packing_house_id: packing_house_id,
        packing_house_process_id: packing_house_process_id,
        container_id: container_id,
      }"
    >
    </go-back-link>

    <bread-crumbs
      :labels="[
        module_name,
        packing_house_process?.name,
        container?.container_code,
      ]"
    />

    <alert-danger :errors_data="std_err" />

    <div v-if="load_general_data" class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>
        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("labels.package_group") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="unassigned_package_groups"
                    label="label"
                    v-model="package_group_id"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.package_group.thermo_code") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model="thermo_code"
                    :class="['input-text', isInvalid('thermo_code')]"
                  />
                </div>
              </div>
            </div>
            <div class="pt-4" v-if="packages.length > 0">
              <label class="label-form">
                {{ $t("model.package_group.packages") }}
              </label>
              <div class="mt-8 flex flex-col">
                <div class="table-first-container">
                  <div class="table-second-container">
                    <div class="table-third-container">
                      <table class="table">
                        <thead class="table-header">
                          <tr>
                            <th scope="col" class="table-header-cell">
                              {{ $t("model.package.week") }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{ $t("model.package.registered_at") }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{ $t("model.package.producer") }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{ $t("model.package.exported_batch") }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{ $t("model.package.quantity") }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{
                                $t(
                                  "model.package.exporter_process_crop_package"
                                )
                              }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{ $t("model.package.exporter_process_brand") }}
                            </th>
                            <th scope="col" class="table-header-cell">
                              {{
                                $t(
                                  "model.package.exporter_process_crop_category"
                                )
                              }}
                            </th>
                          </tr>
                        </thead>
                        <tbody class="table-body">
                          <tr
                            class="table-row"
                            v-for="(package_item, index) in packages"
                            :key="index"
                          >
                            <td class="table-cell-main">
                              {{ package_item.week }}
                            </td>
                            <td class="table-cell">
                              {{ dateFormatter(package_item.registered_at) }}
                            </td>
                            <td class="table-cell">
                              {{ package_item.entity_name }}
                            </td>
                            <td class="table-cell">
                              {{ package_item.exported_batch.batch_code }}
                            </td>
                            <td class="table-cell">
                              {{ package_item.quantity }}
                            </td>
                            <td class="table-cell">
                              {{
                                package_item.exporter_process_crop_package.label
                              }}
                            </td>
                            <td class="table-cell">
                              {{ package_item.exporter_process_brand.name }}
                            </td>
                            <td class="table-cell">
                              {{
                                package_item.exporter_process_crop_category.name
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="gotToShowContainer()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="submit()"
              :label="$t('labels.save_link')"
              :disabled="!can_send"
            />
            <success-button-processing v-else :label="$t('labels.save_link')" />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import Requester from "@/views/packing_house/requesters/production/packing_by_pallet/manage_package_group.js";

export default {
  name: "PHProductionPackingByPalletAddPackageGroup",
  props: {
    packing_house_id: { required: true },
    packing_house_process_id: { required: true },
    container_id: { required: true },
  },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      packing_house_process: null,
      container: null,
      unassigned_package_groups: [],
      packages: [],
      package_group_id: null,
      thermo_code: null,
      row_position: null,
      col_position: null,
    };
  },
  computed: {
    route_query: function () {
      return this.$router.currentRoute._value.query;
    },

    query_package_group_id: function () {
      if ((this.route_query, this.route_query.id)) return this.route_query.id;
      return null;
    },

    query_row: function () {
      if ((this.route_query, this.route_query.row)) return this.route_query.row;
      return null;
    },

    query_col: function () {
      if ((this.route_query, this.route_query.col)) return this.route_query.col;
      return null;
    },

    package_group_params: function () {
      let params = {
        id: this.package_group_id,
        container_id: this.container_id,
        thermo_code: this.thermo_code,
        row_position: this.row_position ?? this.query_row,
        col_position: this.col_position ?? this.query_col,
      };

      if (
        Number(this.query_package_group_id) != Number(this.package_group_id)
      ) {
        params["old_id"] = this.query_package_group_id;
      }

      return params;
    },

    can_send: function () {
      return this.package_group_id && this.container_id;
    },
  },
  methods: {
    loadData: async function () {
      if (this.query_package_group_id) {
        this.package_group_id = Number(this.query_package_group_id);
      }
      this.fetchPackingHouseProcess();
      this.fetchContainer();
      await this.fetchUnassignedPackageGroups();
      this.load_general_data = true;
    },

    fetchUnassignedPackageGroups: async function () {
      let response = await Requester.fetchUnassignedPackageGroups(
        this.packing_house_id,
        this.container_id,
        this.package_group_id
      );
      this.unassigned_package_groups = response.data;
      this.unassigned_package_groups.forEach((group) => {
        group["label"] = `Sem. ${group.week} (${this.dateFormatter(
          group.grouped_at
        )}): ${group.pg_code}`;
      });
    },

    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess(
        this.packing_house_id,
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },

    fetchContainer: async function () {
      let response = await Requester.fetchContainer(
        this.packing_house_id,
        this.container_id
      );
      this.container = response.data;
    },

    fetchPackageGroup: async function () {
      let response = await Requester.fetchPackageGroup(
        this.packing_house_id,
        this.container_id,
        this.package_group_id
      );
      this.thermo_code = response.data.thermo_code;
      this.row_position = response.data.row_position;
      this.col_position = response.data.col_position;
      this.packages = response.data.packages;
    },

    gotToShowContainer: function () {
      this.$router.push({
        name: this.routeName("show_container"),
        params: {
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          container_id: this.container_id,
        },
      });
    },

    submit: async function () {
      if (!this.can_send) return;

      this.proc_save_button = true;
      try {
        let response = await Requester.update(
          this.packing_house_id,
          this.container_id,
          this.package_group_id,
          this.package_group_params
        );

        this.registerSuccessNotification({
          title: response.data.success_message,
        });

        this.gotToShowContainer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.proc_save_button = false;
    },
  },
  created: function () {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.loadData();
      })
    );
  },
  watch: {
    package_group_id: function (newValue) {
      if (newValue) {
        this.fetchPackageGroup();
      }
    },
  },
};
</script>
